import {
  Checkbox,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import React, { FC, useMemo } from 'react';

import { Reminder } from '../../../generated/graphql';
import translate from '../../../i18n/translate';
import { ReminderItem } from '../../../models/reminder';

import MuiIcon from '../MuiIcon';

import { getReminderItems } from './helpers';
import { StyledControl } from './styled';

type Props = {
  isDisabled?: boolean;
  reminders: Reminder[];
  requestOnChange: (reminders: ReminderItem[]) => void;
};

/**
 * Checks if the reminder is already selected
 *
 * @param selected Already selected reminder item
 * @param item     Item that is going to be selected
 */
const getIsSelected = (selected: ReminderItem[], item: ReminderItem) =>
  selected.some(
    v =>
      v.reminder.amount === item.reminder.amount &&
      v.reminder.unit === item.reminder.unit,
  );

/**
 * RemindersPicker component
 *
 * @param props                 Props passed to the component
 * @param props.isDisabled      Whether the picker is disabled
 * @param props.reminders       List of reminders
 * @param props.requestOnChange Callback to change the value of the select
 * @returns                     The component itself
 */
const RemindersPicker: FC<Props> = ({
  isDisabled,
  reminders,
  requestOnChange,
}) => {
  const allReminderItems = useMemo(() => getReminderItems(), []);
  const selectedValues = useMemo(
    () => getReminderItems(reminders),
    [reminders],
  );

  /**
   * Handles the selection and deselection of a reminder item.
   *
   * @param item - The `ReminderItem` to toggle in the selected values.
   */
  const handleItemClick = (item: ReminderItem) => {
    const isSelected = getIsSelected(selectedValues, item);

    const newSelected = isSelected
      ? selectedValues.filter(
          selectedValue => !getIsSelected([item], selectedValue),
        )
      : [...selectedValues, item];

    requestOnChange(newSelected);
  };

  return (
    <StyledControl fullWidth={true}>
      <InputLabel>{translate('REMINDERS')}</InputLabel>
      <Select
        disabled={isDisabled}
        displayEmpty={true}
        input={
          <OutlinedInput
            label={translate('REMINDERS')}
            startAdornment={<MuiIcon icon="clock" style={{ marginRight: 8 }} />}
          />
        }
        multiple={true}
        renderValue={selected => {
          if (selected.length === 0) {
            return translate('GENERAL__ADD_REMINDER');
          }
          return selected.map(v => v.label).join(', ');
        }}
        value={selectedValues}
      >
        {allReminderItems.map(item => {
          const isSelected = getIsSelected(selectedValues, item);

          return (
            <MenuItem
              key={`${item.reminder.amount}-${item.reminder.unit}`}
              onClick={() => handleItemClick(item)}
            >
              <Checkbox checked={isSelected} />
              <ListItemText primary={item.label} />
            </MenuItem>
          );
        })}
      </Select>
    </StyledControl>
  );
};

export default RemindersPicker;
